import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchJson } from '../../../helpers';
import { dashboard_project_filter_my_projects_path, dashboard_project_filter_preset_path } from '~/routes';

const initialState = {
  mode: 'all',
  presetId: null,
  activeProjectIds: [],
  loading: false,
  error: null,
};

// Returns [{ id, project_identifier }] for the current user
export const loadMyProjectIds = createAsyncThunk(
  'projectFilter/loadMyProjects',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchJson(dashboard_project_filter_my_projects_path());
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

// Returns [{ id, project_identifier }] for projects matching the preset filter
export const loadPreset = createAsyncThunk(
  'projectFilter/loadPreset',
  async ({ presetId }, { rejectWithValue }) => {
    try {
      return await fetchJson(dashboard_project_filter_preset_path({ preset_id: presetId }));
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

const projectFilterSlice = createSlice({
  name: 'projectFilter',
  initialState,
  reducers: {
    setMode(state, action) {
      state.mode = action.payload;
      if (action.payload === 'all') {
        state.activeProjectIds = [];
        state.presetId = null;
      }
    },
    clearProjectFilter(state) {
      state.mode = 'all';
      state.presetId = null;
      state.activeProjectIds = [];
      state.error = null;
    },
    setActiveProjectIds(state, action) {
      state.mode = 'custom';
      state.presetId = null;
      state.activeProjectIds = action.payload;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMyProjectIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMyProjectIds.fulfilled, (state, action) => {
        state.loading = false;
        state.activeProjectIds = action.payload || [];
      })
      .addCase(loadMyProjectIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loadPreset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadPreset.fulfilled, (state, action) => {
        state.loading = false;
        state.presetId = action.meta.arg.presetId;
        state.activeProjectIds = action.payload || [];
      })
      .addCase(loadPreset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setMode, clearProjectFilter, setActiveProjectIds } = projectFilterSlice.actions;

export const selectActiveProjectIds = (state) => state.projectFilter.activeProjectIds;
export const selectProjectFilterMode = (state) => state.projectFilter.mode;
export const selectProjectFilterActive = (state) => state.projectFilter.mode !== 'all';
export const selectProjectFilterLoading = (state) => state.projectFilter.loading;
export const selectProjectFilterPresetId = (state) => state.projectFilter.presetId;

export default projectFilterSlice.reducer;
